import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '@/store';
import type { FileCloneStatus } from '@/types/poc/helia';

const selectHeliaFiles = (state: RootState) => state.helia.files;
const selectCloneQueue = (state: RootState) => state.cloneQueue.queue;
const selectCurrentlyCloning = (state: RootState) => state.cloneQueue.currentlyCloning;
const selectFailedFiles = (state: RootState) => state.cloneQueue.failedFiles;

/**
 * Creates a map of CID -> FileCloneStatus for determining clone state of files.
 *
 * Status priority:
 * - 'cloned': File exists in local Helia blockstore (ready to decrypt/download)
 * - 'cloning': File is currently being cloned from the network
 * - 'pending': File is queued for cloning
 * - 'failed': Clone attempt failed
 * - 'unknown': File not tracked (not synced yet)
 */
export const selectFileCloneStatusMap = createSelector(
  [selectHeliaFiles, selectCloneQueue, selectCurrentlyCloning, selectFailedFiles],
  (heliaFiles, queue, currentlyCloning, failedFiles): Record<string, FileCloneStatus> => {
    const statusMap: Record<string, FileCloneStatus> = {};

    // Mark all files in Helia as cloned
    for (const cid of Object.keys(heliaFiles)) {
      statusMap[cid] = 'cloned';
    }

    // Mark currently cloning file (overwrites if somehow in helia already)
    if (currentlyCloning !== null) {
      statusMap[currentlyCloning.cid] = 'cloning';
    }

    // Mark queued files as pending (only if not already cloned or cloning)
    for (const queuedFile of queue) {
      statusMap[queuedFile.cid] ??= 'pending';
    }

    // Mark failed files (only if not already cloned, cloning, or pending)
    for (const cid of Object.keys(failedFiles)) {
      statusMap[cid] ??= 'failed';
    }

    return statusMap;
  }
);

/**
 * Creates a map of CID -> error message for files that failed to clone.
 */
export const selectFileCloneErrorMap = createSelector(
  [selectFailedFiles],
  (failedFiles): Record<string, string> => failedFiles
);

/**
 * Get the clone status for a specific file CID.
 * Returns 'unknown' if the file is not tracked.
 */
export const selectFileCloneStatus = (cid: string) =>
  createSelector(selectFileCloneStatusMap, (statusMap): FileCloneStatus =>
    statusMap[cid] ?? 'unknown'
  );
